'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Children, Component } from 'react';
import PropTypes from 'prop-types';
import invariant from 'react-utils/invariant';
import { findDOMNode } from 'react-dom';
import styled from 'styled-components';
import { OBSIDIAN } from 'HubStyleTokens/colors';
import { remCalc } from 'UIComponents/core/Style';
const ScrollContainer = styled.div.withConfig({
  displayName: "HorizontalScrollContainer__ScrollContainer",
  componentId: "lop23r-0"
})(["overflow:hidden;flex:1 1 auto;&:hover .horizontal-scroll-container__scroll-bar,&:active .horizontal-scroll-container__scroll-bar{background-color:", ";}"], OBSIDIAN);
const ScrollWrapper = styled.div.withConfig({
  displayName: "HorizontalScrollContainer__ScrollWrapper",
  componentId: "lop23r-1"
})(["overflow:hidden;"]);
const ScrollBar = styled.div.attrs(props => ({
  style: Object.assign({}, props, {
    display: props.width ? 'inherit' : 'none'
  })
})).withConfig({
  displayName: "HorizontalScrollContainer__ScrollBar",
  componentId: "lop23r-2"
})(["height:", ";position:relative;z-index:1000;bottom:", ";border-radius:3px;"], remCalc('4px'), remCalc('4px'));
export default class HorizontalScrollContainer extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      width: 0,
      left: 0
    };

    this.onMouseMove = evt => {
      const {
        scrollWidth,
        clientWidth
      } = this.__wrapperNode;
      const deltaX = (evt.pageX - this.startX) * (scrollWidth / clientWidth);
      const newScroll = this.scrollStart + deltaX;
      this.__wrapperNode.scrollLeft = newScroll;
      requestAnimationFrame(() => {
        this.updateScrollBarPosition();
      });
    };

    this.onMouseDown = evt => {
      const {
        scrollLeft
      } = this.__wrapperNode;
      this.startX = evt.pageX;
      this.scrollStart = scrollLeft;
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    };

    this.onMouseUp = () => {
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
    };

    this.setRef = ref => {
      this.__wrapperRef = ref;
    };

    this.updateWidth = () => {
      if (!this.__wrapperNode) return;
      setTimeout(() => {
        const {
          clientWidth,
          scrollWidth
        } = this.__wrapperNode;

        if (clientWidth >= scrollWidth) {
          if (this.state.width === null) return;
          this.setState({
            width: null
          });
        } else {
          const width = Math.round(clientWidth / scrollWidth * clientWidth);
          if (this.state.width === width) return;
          this.setState({
            width
          });
        }
      }, 0);
    };

    this.updateScrollBarPosition = () => {
      const {
        scrollWidth,
        scrollLeft,
        clientWidth
      } = this.__wrapperNode;
      const scrollPercent = scrollLeft / (scrollWidth - clientWidth);
      const deltaWidth = clientWidth - this.state.width;
      const deltaLeft = Math.round(scrollPercent * deltaWidth);

      if (this.state.left !== deltaLeft) {
        this.setState({
          left: deltaLeft
        });
      }
    };

    this.onWheel = evt => {
      /* We have a lot of 0 and -0 for deltaX/Y due to inertia scrolling */
      evt.preventDefault();
      const {
        deltaX,
        deltaY
      } = evt;
      window.requestAnimationFrame(() => {
        if (Math.abs(deltaY) > 1) {
          this.__wrapperNode.scrollLeft -= deltaY;
        } else if (Math.abs(deltaX) > 1) {
          this.__wrapperNode.scrollLeft += deltaX;
        }

        this.updateScrollBarPosition();
      });
    };
  }

  componentDidMount() {
    this.__wrapperNode = findDOMNode(this.__wrapperRef);
    window.addEventListener('resize', this.updateWidth);

    this.__wrapperRef.addEventListener('wheel', this.onWheel, {
      passive: false
    });

    this.updateWidth();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWidth);

    this.__wrapperRef.removeEventListener('wheel', this.onWheel, {
      passive: false
    });
  }
  /* Drag handlers */


  render() {
    const {
      children
    } = this.props;
    const childrenCount = Children.count(children);
    invariant(childrenCount === 1, 'Expected child count to be 1 but got %s.', childrenCount);
    this.updateWidth();
    return /*#__PURE__*/_jsxs(ScrollContainer, {
      children: [/*#__PURE__*/_jsx(ScrollWrapper, {
        ref: this.setRef,
        children: children
      }), /*#__PURE__*/_jsx(ScrollBar, Object.assign({}, this.state, {
        className: "horizontal-scroll-container__scroll-bar",
        onMouseDown: this.onMouseDown
      }))]
    });
  }

}
HorizontalScrollContainer.propTypes = {
  children: PropTypes.node.isRequired
};